import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import {
  useNavigation,
  CompositeNavigationProp,
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Platform } from 'react-native';
import Snackbar from '~/components/Snackbar';
import { useAuth } from '~/context/auth';
import { useIntercom } from '~/context/intercom';
import { LINKS } from '~/data/constants';
import { DATA_POLICY, TERM } from '~/data/derivedConstants';
import useUserGroups from '~/data/hooks/useUserGroups';
import { GROUP } from '~/data/operations/group';
import { ProfileStackParamList, RootStackParamList } from '~/navigation/types';
import {
  getCurrentVersion,
  getInviteUrl,
  versionNeedUpdated,
} from '~/utils/helpers';
import useCopyToClipboard from '~/utils/hooks/useCopyToClipboard';
import { t } from '~/utils/i18n';
import { shareText } from '~/utils/index';
import ProfileLayout from './layout';

type ProfileNavProp = CompositeNavigationProp<
  StackNavigationProp<ProfileStackParamList, 'Profile'>,
  StackNavigationProp<RootStackParamList>
>;

export default function Profile(): JSX.Element {
  const navigation = useNavigation<ProfileNavProp>();

  const [needUpdate, setNeedUpdate] = useState<boolean | undefined>();
  const { authGroupId, authUserGroup, coreAdmin, authUserId } = useAuth();
  const { unReadMessageCount, onMessenger, onHelpCenter } = useIntercom();
  const { copy } = useCopyToClipboard();

  const { data: groupData } = useQuery(GROUP, {
    skip: !authGroupId,
    variables: {
      id: authGroupId,
    },
  });

  const group = groupData?.group;
  const groupId = group?.id;
  const token = group?.token;
  const inviteUrl = groupId && token ? getInviteUrl(groupId, token) : '';

  const version = getCurrentVersion(true);

  useEffect(() => {
    versionNeedUpdated().then((isNeeded) => {
      setNeedUpdate(isNeeded);
    });
  }, []);

  const { pendingUserGroups, onAnswerRequest } = useUserGroups();

  const openBannersInBrowser = (url: string) => {
    navigation.navigate('Browser', {
      title: 'Banner',
      url,
    });
  };

  const onShareLink = async () => {
    if (Platform.OS === 'web') {
      const success = await copy(inviteUrl);
      if (success) {
        Snackbar.show(t('clipboardCopy.groupInvite'));
      }
      return;
    }

    shareText(inviteUrl);
  };

  return (
    <ProfileLayout
      unReadMessageCount={unReadMessageCount}
      userGroup={authUserGroup}
      group={group}
      coreAdmin={coreAdmin}
      version={version}
      needUpdate={needUpdate}
      openBannersInBrowser={openBannersInBrowser}
      pendingUserGroups={pendingUserGroups}
      onDeclineUser={(userId) => onAnswerRequest(userId, false)}
      onAcceptUser={(userId) => onAnswerRequest(userId, true)}
      onSettings={() => navigation.navigate('GroupSetup')}
      onShareLink={onShareLink}
      onHelpCenter={onHelpCenter}
      onContact={onMessenger}
      onEditProfile={() => navigation.navigate('ProfileSetup')}
      onNotification={() => navigation.navigate('NotificationSettings')}
      onAppSetup={() => navigation.navigate('AppSetup')}
      onRateApp={() =>
        navigation.navigate('Browser', {
          title: '',
          url: LINKS.RATE,
        })
      }
      onImprintAndLicences={() =>
        navigation.navigate('Browser', {
          title: TERM.title,
          url: TERM.url,
        })
      }
      onDataPolicy={() =>
        navigation.navigate('Browser', {
          title: DATA_POLICY.title,
          url: DATA_POLICY.url,
        })
      }
      authUserId={authUserId}
    />
  );
}
